
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { HistologyImage } from '../types';
import { Button } from '../components/ui/Button';
import { ChevronLeft, ChevronRight, RotateCw, RefreshCcw, Layers, Microscope } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/utils';

const FlashcardsPage = () => {
  const { pdfId } = useParams();
  const navigate = useNavigate();
  const { images } = useAppStore();
  const [deck, setDeck] = useState<HistologyImage[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    let source = images;
    if (pdfId) {
      source = images.filter(img => img.pdf_id === pdfId);
    }
    
    if (source.length > 0) {
      setDeck([...source].sort(() => 0.5 - Math.random()));
    } else {
      setDeck([]);
    }
  }, [images, pdfId]);

  const handleNext = () => {
    setIsFlipped(false);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % deck.length);
    }, 150);
  };

  const handlePrev = () => {
    setIsFlipped(false);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + deck.length) % deck.length);
    }, 150);
  };

  const handleShuffle = () => {
    setIsFlipped(false);
    setDeck([...deck].sort(() => 0.5 - Math.random()));
    setCurrentIndex(0);
  };

  if (deck.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center px-6">
        <Layers className="h-10 w-10 text-slate-300 mb-4" />
        <h3 className="text-xl font-bold text-slate-900">No Slides Available</h3>
        <p className="text-slate-500 mb-6 max-w-xs text-sm">
          Please upload and extract histology slides to populate this deck.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/library')}>Library</Button>
          <Button size="sm" onClick={() => navigate('/upload')}>Uploader</Button>
        </div>
      </div>
    );
  }

  const currentImage = deck[currentIndex];

  return (
    <div className="max-w-xl mx-auto space-y-8 px-4 sm:px-0 pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Study Session</h1>
          <p className="text-slate-500 text-xs font-medium">Recognize the histology pattern to identify.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleShuffle} className="p-2 text-slate-400 hover:text-primary-500 transition-colors" title="Shuffle Deck">
            <RefreshCcw className="h-4 w-4" />
          </button>
          <div className="bg-white border border-slate-200 px-3 py-1 rounded-full text-[10px] font-bold text-slate-500">
            {currentIndex + 1} / {deck.length}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-8">
        {/* 
          Removed touch-none and added touch-pan-y to allow vertical scrolling 
          even if the user starts the swipe on the card itself.
        */}
        <div 
          className="relative w-full h-[400px] sm:h-[450px] perspective-1000 cursor-pointer touch-pan-y"
          onClick={() => setIsFlipped(!isFlipped)}
        >
          <motion.div 
            className="relative w-full h-full preserve-3d will-change-transform"
            animate={{ rotateY: isFlipped ? 180 : 0 }}
            transition={{ duration: 0.6, type: 'spring', stiffness: 260, damping: 20 }}
          >
            {/* FRONT: IMAGE */}
            <div 
              className="absolute inset-0 bg-white rounded-[2rem] ring-1 ring-slate-200 shadow-[0_10px_40px_rgba(0,0,0,0.08)] backface-hidden flex flex-col overflow-hidden"
              style={{ WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden' }}
            >
              <div className="flex-1 bg-slate-50 flex items-center justify-center p-6 min-h-0">
                <img 
                  src={currentImage.image_url} 
                  className="max-w-full max-h-full object-contain rounded-xl shadow-sm" 
                  draggable={false}
                  alt="Histology slide"
                />
              </div>
              <div className="p-5 bg-white text-center border-t border-slate-100 shrink-0">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">IDENTIFY SLIDE</p>
                <p className="text-[9px] text-slate-300 mt-1 uppercase font-bold truncate px-4">{currentImage.original_filename}</p>
              </div>
            </div>

            {/* BACK: LABEL */}
            <div 
              className="absolute inset-0 bg-primary-600 rounded-[2rem] shadow-[0_20px_60px_rgba(37,99,235,0.25)] backface-hidden flex flex-col items-center justify-center p-8 text-center text-white"
              style={{ 
                transform: 'rotateY(180deg)', 
                WebkitBackfaceVisibility: 'hidden', 
                backfaceVisibility: 'hidden' 
              }}
            >
              <div className="bg-white/10 p-4 rounded-2xl mb-6 backdrop-blur-sm">
                <Microscope className="h-8 w-8 text-primary-100" />
              </div>
              <p className="text-[10px] font-black text-primary-200 uppercase tracking-[0.3em] mb-4">Verification</p>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight">{currentImage.identified_organ_tissue}</h2>
              
              <div className="mt-12 flex gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary-300"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-primary-300/50"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-primary-300/20"></div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Navigation Controls */}
        <div className="flex items-center gap-4 w-full">
          <button 
            onClick={(e) => { e.stopPropagation(); handlePrev(); }} 
            className="flex-1 h-14 bg-white ring-1 ring-slate-200 rounded-2xl flex items-center justify-center hover:bg-slate-50 transition-colors active:scale-95 shadow-sm"
          >
            <ChevronLeft className="h-6 w-6 text-slate-400" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); handleNext(); }} 
            className="flex-1 h-14 bg-white ring-1 ring-slate-200 rounded-2xl flex items-center justify-center hover:bg-slate-50 transition-colors active:scale-95 shadow-sm"
          >
            <ChevronRight className="h-6 w-6 text-slate-400" />
          </button>
        </div>
        
        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest pb-10">Tap card to reveal identification</p>
      </div>
    </div>
  );
};

export default FlashcardsPage;
